import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";

type SpinnerProps = {
  container?: "screen" | "parent";
  size?: number;
  color?: string;
  label?: string;
  show?: boolean;
  overlay?: boolean;
  overlayClassName?: string;
};

const Spinner: React.FC<SpinnerProps> = ({
  container = "screen",
  size = 48,
  color = "text-primary-600",
  label,
  show = false,
  overlay = true,
  overlayClassName,
}) => {
  const wrapperClasses =
    container === "screen" ? "fixed inset-0 z-50" : "absolute inset-0";

  const spinnerColor =
    color && !(color.startsWith("#") || color.startsWith("rgb"))
      ? color
      : undefined;
  const svgColor =
    color && (color.startsWith("#") || color.startsWith("rgb"))
      ? color
      : undefined;

  return (
    <AnimatePresence>
      {show && (
        <output
          className={clsx(wrapperClasses, "flex items-center justify-center")}
          aria-live="polite"
          aria-busy="true"
        >
          <motion.div
            className="flex items-center justify-center w-full h-full"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {overlay && (
              <motion.div
                className={clsx(
                  "absolute z-0 inset-0 bg-gray-900/30 dark:bg-black/40 backdrop-blur-sm transition-all",
                  overlayClassName,
                )}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              />
            )}
            <motion.div
              className="relative z-10 flex flex-col items-center gap-3"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              {/* Modern SVG spinner */}
              <svg
                className={clsx(
                  "animate-spin-smooth drop-shadow-lg",
                  spinnerColor,
                )}
                style={{ width: size, height: size, color: svgColor }}
                viewBox="0 0 50 50"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <circle
                  cx="25"
                  cy="25"
                  r="20"
                  stroke="currentColor"
                  strokeWidth="5"
                  opacity="0.2"
                />
                <motion.circle
                  cx="25"
                  cy="25"
                  r="20"
                  stroke="currentColor"
                  strokeWidth="5"
                  strokeLinecap="round"
                  strokeDasharray="100 60"
                  initial={{ strokeDashoffset: 160 }}
                  animate={{ strokeDashoffset: 60 }}
                  transition={{
                    repeat: Infinity,
                    duration: 1.2,
                    ease: "linear",
                  }}
                />
              </svg>
              {label && (
                <span className="text-base font-medium text-gray-800 dark:text-gray-200 select-none">
                  {label}
                  <span className="sr-only">Loading...</span>
                </span>
              )}
            </motion.div>
          </motion.div>
        </output>
      )}
    </AnimatePresence>
  );
};

export default Spinner;
